import React, { useState, useEffect } from "react";
import { Header } from "../../internal/Header/Header.js";
import Cookies from "./Cookies.js";
import useIfLoggedIn from "../../../hooks/useIfLoggedIn";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import HeroBanner from "../NewLandingPage/components/HeroBanner.js";
import Offerings from "../NewLandingPage/components/Offerings.js";
import About from "../NewLandingPage/components/About.js";
import BoardOfDirectors from "../NewLandingPage/components/BoardOfDirectors.js";
import JustActComponent from "../NewLandingPage/components/JustactWorks.js";
import ODRInsightsComponent from "../NewLandingPage/components/ODRInsightsComponent.js";
import AccreditationUI from "../NewLandingPage/components/Accreditation.js";
import Footer from "../NewLandingPage/components/Footer.js";
import { PageLoader } from "../NewLandingPage/components/PageLoader.js";
import { Images } from "../../../assets/images.js";
import useCartCount from "../../../hooks/useCartCount.js";

const CRITICAL_IMAGE_KEYS = [
  "LoanDispute",
  "Transcription",
  "CaseManagement",
  "CloudBasedOdr",
  "CommercialDisputes",
  "FamilyDisputes",
  "JustActMailers",
];

const Index = (props) => {
  useIfLoggedIn();

  const [criticalImagesLoaded, setCriticalImagesLoaded] = useState(false);
  const { scrollHomeTick } = useCartCount();

  const section = props.location.hash.replace("#", "");

  // Scroll to section when hash changes
  useEffect(() => {
    if (!section) return;

    if (section === "home") {
      requestAnimationFrame(() =>
        window.scrollTo({ top: 0, behavior: "smooth" }),
      );
      return;
    }

    const element = document.getElementById(section);
    if (!element) return;

    requestAnimationFrame(() => {
      const y = element.getBoundingClientRect().top + window.pageYOffset - 70;
      window.scrollTo({ top: y, behavior: "smooth" });
    });
  }, [section, scrollHomeTick]);

  // Preload critical images and reveal page content
  useEffect(() => {
    const criticalImages = CRITICAL_IMAGE_KEYS.map(
      (key) => Images?.[key],
    ).filter(Boolean);

    if (criticalImages.length === 0) {
      setCriticalImagesLoaded(true);
      return;
    }

    let loadedCount = 0;
    const total = criticalImages.length;

    const markLoaded = () => {
      loadedCount += 1;
      if (loadedCount === total) {
        setTimeout(() => setCriticalImagesLoaded(true), 300);
      }
    };

    criticalImages.forEach((src) => {
      const img = new Image();
      img.onload = markLoaded;
      img.onerror = markLoaded; // still show page even if an image 404s
      img.src = src;
    });

    // Absolute fallback — show content after 3 s regardless
    const fallback = setTimeout(() => setCriticalImagesLoaded(true), 3000);
    return () => clearTimeout(fallback);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Helmet>
        {Images?.LoanDispute && (
          <link rel="preload" as="image" href={Images.LoanDispute} />
        )}
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <title>
          Online Dispute Resolution (ODR) Services - Online Arbitration &amp;
          Mediation
        </title>
        <meta
          name="description"
          content="IAMC is an Online Dispute Resolution (ODR) platform and is set up by lawyers & professionals. Resolve disputes out of courts through negotiation, mediation and arbitration"
        />
        <meta
          name="keywords"
          content="Online Dispute Resolution, ODR Services, Online Arbitration & Mediation, Dispute Resolution Online, Alternative Dispute Resolution, ADR Services, Online Dispute Resolution in India, IAMC"
        />
      </Helmet>

      <PageLoader isLoading={!criticalImagesLoaded} />

      <Header LandingPage NavPages>
        <PageContainer
          id="home"
          style={{
            opacity: criticalImagesLoaded ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
          }}
        >
          <Cookies />
          {/* HeroBanner is full-bleed — no side padding applied here */}
          <HeroBanner />
          {/* Remaining sections get their own internal padding */}
          <Offerings props={props} />
          <About />
          <BoardOfDirectors />
          <JustActComponent />
          <ODRInsightsComponent
            CommercialDisputes={Images.CommercialDisputes}
            FamilyDisputes={Images.FamilyDisputes}
            JustActMailers={Images.JustActMailers}
          />
          <AccreditationUI props={props} />
          <Footer />
        </PageContainer>
      </Header>
    </>
  );
};

export default Index;

const PageContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 0 auto;
  background: #ffffff;
  /*
    FIX: No horizontal padding on the container — each section handles its
    own padding.  The previous "padding: 0 10px" on mobile was causing the
    HeroBanner / JustAsk chat to be 20 px narrower than the viewport, which
    showed white strips on the sides and clipped the chat input.
  */
  overflow-x: hidden;
  box-sizing: border-box;
`;
